import { Link } from "react-router-dom";
import {
  ShoppingBag,
  ArrowUpRight,
  Truck,
  Shield,
  Package,
  Sparkles,
  Mic,
} from "lucide-react";
import { MERCH_URL, SOCIALS } from "@/data/site";
import StudioBackdrop from "@/components/podcast/StudioBackdrop";
import { Headphones, CinemaCamera } from "@/components/podcast/StudioGear";

// Each item is rendered as a typography-driven design tile. When the real
// Printful store goes live with photos, swap the Tile renderer for an <img>.
const MERCH = [
  {
    type: "TEE",
    name: "Prove Your Point — Logo Tee",
    palette: { bg: "#0A0A0A", primary: "#E10600", secondary: "#1E6FE0", text: "#FFFFFF" },
    badge: "Best Seller",
    price: "$32",
  },
  {
    type: "HOODIE",
    name: "His Side. Her Side. Hoodie",
    palette: { bg: "#1E6FE0", primary: "#FFFFFF", secondary: "#E10600", text: "#FFFFFF" },
    badge: "New",
    price: "$58",
  },
  {
    type: "CAP",
    name: "PYP Dad Cap",
    palette: { bg: "#0A0A0A", primary: "#FFFFFF", secondary: "#E10600", text: "#FFFFFF" },
    price: "$28",
  },
  {
    type: "TEE",
    name: "Be Prepared To Prove It",
    palette: { bg: "#E10600", primary: "#FFFFFF", secondary: "#0A0A0A", text: "#FFFFFF" },
    price: "$32",
  },
  {
    type: "MUG",
    name: "♂ × ♀ Mic Mug",
    palette: { bg: "#FFFFFF", primary: "#E10600", secondary: "#1E6FE0", text: "#0A0A0A" },
    price: "$18",
  },
  {
    type: "TOTE",
    name: "Studio Tote — NYC × Boston",
    palette: { bg: "#0A0A0A", primary: "#1E6FE0", secondary: "#E10600", text: "#FFFFFF" },
    price: "$24",
  },
];

function DesignTile({ item }) {
  const { type, name, palette } = item;
  const isLight = palette.bg === "#FFFFFF";

  return (
    <div
      className="relative w-full aspect-square overflow-hidden"
      style={{ background: palette.bg }}
    >
      {/* halftone */}
      <div
        className="absolute inset-0 opacity-25"
        style={{
          backgroundImage: `radial-gradient(circle, ${palette.primary} 1.2px, transparent 1.5px)`,
          backgroundSize: "12px 12px",
        }}
      />

      {/* corner accents */}
      <div className="absolute top-0 left-0 h-1 w-12" style={{ background: palette.primary }} />
      <div className="absolute top-0 left-0 w-1 h-12" style={{ background: palette.primary }} />
      <div className="absolute bottom-0 right-0 h-1 w-12" style={{ background: palette.secondary }} />
      <div className="absolute bottom-0 right-0 w-1 h-12" style={{ background: palette.secondary }} />

      {/* Product type tag */}
      <div
        className="absolute top-4 right-4 px-2 py-1 text-[10px] tracking-[0.25em] uppercase font-bold font-mono border"
        style={{ borderColor: palette.primary, color: palette.primary }}
      >
        {type}
      </div>

      {/* gendered symbol watermark */}
      <span
        aria-hidden
        className="absolute -bottom-8 -left-4 leading-none font-black select-none pointer-events-none"
        style={{ color: palette.secondary, opacity: 0.12, fontSize: "14rem" }}
      >
        {type === "MUG" ? "☕" : type === "CAP" ? "PYP" : type === "TOTE" ? "♀♂" : "♂"}
      </span>

      {/* Design text centered */}
      <div className="absolute inset-0 flex flex-col justify-center items-center px-6 text-center">
        <p
          className="text-[10px] tracking-[0.3em] uppercase font-bold font-mono"
          style={{ color: palette.primary }}
        >
          Prove Your Point
        </p>
        <p
          className="font-display uppercase leading-[0.85] mt-3 text-4xl md:text-5xl"
          style={{ color: palette.text }}
        >
          {name.split(" ").slice(0, 2).join(" ")}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <span className="font-display text-2xl leading-none" style={{ color: palette.primary }}>♀</span>
          <Mic
            className="w-5 h-5"
            style={{ color: isLight ? "#0A0A0A" : "#FFFFFF" }}
          />
          <span className="font-display text-2xl leading-none" style={{ color: palette.secondary }}>♂</span>
        </div>
      </div>

      {/* mini waveform */}
      <div className="absolute bottom-3 left-3 right-12 flex items-end gap-[2px] h-3 opacity-70">
        {Array.from({ length: 24 }).map((_, i) => {
          const seed = Math.abs(Math.sin((i + name.length) * 0.6));
          return (
            <span
              key={i}
              className="flex-1"
              style={{
                background: i % 2 === 0 ? palette.primary : palette.secondary,
                height: `${25 + seed * 75}%`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export default function Merch() {
  return (
    <div data-testid="merch-page" className="pt-32 pb-20 min-h-screen bg-[#0A0A0A] relative film-grain overflow-hidden">
      <StudioBackdrop opacity={0.14} size={260} />
      <Headphones
        className="absolute -right-24 top-40 w-[28rem] hidden lg:block float-slow"
        color="#E10600"
        strokeWidth={1}
        style={{ opacity: 0.07 }}
      />
      <CinemaCamera
        className="absolute -left-20 bottom-32 w-96 hidden lg:block"
        color="#1E6FE0"
        strokeWidth={1}
        style={{ opacity: 0.07 }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="text-xs tracking-[0.25em] uppercase font-bold mb-3 flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-[#E10600]" />
              <span className="text-[#E10600]">Official Merch</span>
              <span className="text-zinc-600">·</span>
              <span className="text-[#1E6FE0]">Powered by Printful</span>
            </p>
            <h1 className="font-display text-6xl md:text-8xl uppercase tracking-tight leading-[0.9]">
              Rep <span className="text-[#1E6FE0]">The</span> <span className="text-[#E10600]">Show</span>
            </h1>
            <p className="mt-6 text-zinc-300 text-lg max-w-2xl">
              Tees, hoodies, caps, and more — designed for the audience that listens with
              their head and their gut. Every drop helps keep the conversation independent
              and ad-light.
            </p>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <a
              href={MERCH_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="merch-shop-cta-top"
              className="btn-primary"
            >
              Shop The Store
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </header>

        {/* Trust strip */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#262626] border border-[#262626] mb-16">
          <TrustTile icon={Truck} title="Worldwide Shipping" body="Printed & shipped from the closest fulfillment hub." color="#E10600" />
          <TrustTile icon={Shield} title="Quality Guaranteed" body="Premium fabrics, reactive inks, real durability." color="#1E6FE0" />
          <TrustTile icon={Package} title="Made On Demand" body="Less waste. No deadstock. Printed when you order." color="#E10600" />
          <TrustTile icon={Sparkles} title="Limited Drops" body="Some designs are seasonal — when they're gone, they're gone." color="#1E6FE0" />
        </section>

        {/* Product grid */}
        <section className="mb-20">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-2">
                The Drop
              </p>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
                Latest <span className="text-[#E10600]">Designs</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
            {MERCH.map((item, i) => (
              <a
                key={item.name}
                href={MERCH_URL}
                target="_blank"
                rel="noreferrer"
                data-testid={`merch-item-${i}`}
                className="group bg-[#0A0A0A] hover:bg-[#121212] flex flex-col transition-colors"
              >
                <div className="relative">
                  <DesignTile item={item} />
                  {item.badge && (
                    <span
                      className="absolute top-3 left-3 px-2 py-1 text-[10px] font-bold tracking-widest text-white"
                      style={{ background: item.badge === "New" ? "#1E6FE0" : "#E10600" }}
                    >
                      {item.badge}
                    </span>
                  )}
                  {/* hover shop indicator */}
                  <div className="absolute bottom-3 right-3 w-11 h-11 border border-white/40 bg-black/40 backdrop-blur-sm flex items-center justify-center group-hover:bg-[#E10600] group-hover:border-[#E10600] transition-all">
                    <ShoppingBag className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div className="p-5 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono">
                      {item.type}
                    </p>
                    <h3 className="mt-1.5 font-display text-xl uppercase leading-tight text-white group-hover:text-[#E10600] transition-colors">
                      {item.name}
                    </h3>
                  </div>
                  <p className="font-display text-2xl text-[#E10600] leading-none shrink-0">
                    {item.price}
                  </p>
                </div>
              </a>
            ))}
          </div>

          <p className="mt-6 text-xs text-zinc-500 font-mono">
            * Design previews shown. Live product photos & full color/size options on the
            Printful store.
          </p>
        </section>

        {/* Big "Shop on Printful" CTA block */}
        <section
          data-testid="merch-shop-cta-block"
          className="relative border border-[#262626] overflow-hidden p-10 md:p-16 mb-16"
        >
          <div className="absolute -top-16 -right-16 w-72 h-72 halftone opacity-30 pointer-events-none" />
          <div className="absolute -bottom-16 -left-16 w-72 h-72 halftone-blue opacity-30 pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#1E6FE0] mb-3">
                Hit The Store
              </p>
              <h2 className="font-display text-4xl md:text-6xl uppercase leading-none">
                Shop The Full <span className="text-[#E10600]">Catalog</span>
              </h2>
              <p className="mt-5 text-zinc-300 max-w-xl">
                Every design printed-on-demand by Printful — premium fabrics, real fits,
                and worldwide shipping. New drops every season.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <a
                href={MERCH_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="merch-shop-cta-main"
                className="btn-primary"
              >
                Shop On Printful
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Bundle / collab callouts */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
          <Callout
            title="Group Orders & Bulk"
            body="Hosting a watch party or running a brand activation? Discounts on 10+ units."
            cta="Email Us"
            href={`mailto:${SOCIALS.email}?subject=Bulk Merch Inquiry`}
            color="#E10600"
            testid="merch-bulk"
          />
          <Callout
            title="Collaboration Drops"
            body="Designer, artist, or brand wanting to collab on a limited capsule? We're open."
            cta="Pitch A Collab"
            href="/sponsorship"
            internal
            color="#1E6FE0"
            testid="merch-collab"
          />
        </section>
      </div>
    </div>
  );
}

function TrustTile({ icon: Icon, title, body, color }) {
  return (
    <div className="bg-[#0A0A0A] p-5 md:p-6">
      <Icon className="w-6 h-6 mb-3" style={{ color }} />
      <p className="font-display text-lg md:text-xl uppercase leading-none text-white">
        {title}
      </p>
      <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function Callout({ title, body, cta, href, internal = false, color, testid }) {
  const inner = (
    <>
      <p className="text-[10px] tracking-[0.25em] uppercase font-bold font-mono" style={{ color }}>
        {title}
      </p>
      <h3 className="mt-2 font-display text-3xl md:text-4xl uppercase leading-tight text-white">
        {title}
      </h3>
      <p className="mt-3 text-zinc-400">{body}</p>
      <span
        className="mt-6 inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase"
        style={{ color }}
      >
        {cta}
        <ArrowUpRight className="w-4 h-4" />
      </span>
    </>
  );
  const className =
    "bg-[#0A0A0A] hover:bg-[#121212] p-8 md:p-10 transition-colors block";
  if (internal) {
    return (
      <Link to={href} data-testid={testid} className={className}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} data-testid={testid} className={className}>
      {inner}
    </a>
  );
}
